/* eslint-disable camelcase */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import WebFont from 'webfontloader';
import PropTypes from 'prop-types';

import { hideIntroPage, updateRespondentEmail, updateRespondentQuizTimerInfo } from '../../reducer/form';
import { defaultUserCustomTexts, toHex } from '../../../../shared/form-lib';
import { formStyles, fplsFetch, replaceAt } from '../../../../shared/utils';
import { FormContext } from '../../../shared/contexts/form-context';
import FormHeader from '../../../partials/form-preview/form-header';
import { selectForm, selectFormDesign } from '../../selectors';
import {
  IntroBody,
  FormTitle,
  IntroWrapper,
  RespondentInfo,
  MarkDownWrapper,
  FormPreRequisite,
} from './intro-page.styled';

const quizIdRecognitionError =
  'ID not recognized. You may not be registered, might have already taken the quiz, or there could be a spelling error.';

const RESPONDENT_EMAIL_HINTS = {
  both: 'Enter a valid email to receive your form responses and a link to complete your submission if needed.',
  paymentOnly: 'Enter your email to receive a secure link to complete your submission if needed.',
  collectOnly: 'Enter a valid email to receive your form responses.',
};

const IntroPage = ({ handleDemoStartForm }) => {
  const quizIdRef = useRef();
  const dispatch = useDispatch();
  const emailRef = useRef();
  const [quizIdError, setQuizIdError] = useState('');
  const isEditedByUser = window.location.search?.includes('user');
  const { inBuilder, inCustomise, inForm } = useContext(FormContext);
  const [isProcessingQuizId, setIsProcessingQuizId] = useState(false);

  const {
    name,
    design,
    form_font,
    form_options,
    intro_message,
    form_text_color,
    intro_alignment,
    underline_title,
    form_title_font_size,
    intro_page_button_text,
    user_custom_texts = {},
    intro_page_button_color,
    submission_button_background,
    collect_user_email = false,
  } = useSelector((state) => selectForm(state));

  const {
    builderForm,
    introDetails: builderIntroDetails,
    enableIntroPage: builderEnableIntroPage,
  } = useSelector((state) => state.builder.present);

  const formDesign = useSelector((state) => selectFormDesign(state));
  const newCss = replaceAt(formDesign.old.form_external_css, 0, '');
  const { requestQuizUniqueId = false, reshowIntroPage, hasPaymentField } = useSelector((state) => state.form);

  const {
    enable_timer: enableTimer,
    timer_options: { timer_purpose: timerPurpose, timer_unique_ids: timerUniqueIds } = {},
    enable_intro_page: formEnableIntroPage,
  } = form_options || {};

  const collectRespondentEmail = collect_user_email || hasPaymentField;
  const userEnableIntroPage = inBuilder ? builderEnableIntroPage : formEnableIntroPage;
  const userIntroMessage = inBuilder ? builderIntroDetails.intro_message : intro_message;
  const hasIntroMessage = userEnableIntroPage && userIntroMessage ? true : false;
  const userIntroButtonText = inBuilder ? builderIntroDetails.intro_page_button_text : intro_page_button_text;
  const userIntroButtonColor = inBuilder ? builderIntroDetails.intro_page_button_color : intro_page_button_color;
  const showFormPreRequisites = !isEditedByUser && !reshowIntroPage && (collectRespondentEmail || requestQuizUniqueId);

  const collectRespondentEmailHint = () => {
    if (collect_user_email && hasPaymentField) return RESPONDENT_EMAIL_HINTS.both;
    if (collect_user_email) return RESPONDENT_EMAIL_HINTS.collectOnly;
    if (hasPaymentField) return RESPONDENT_EMAIL_HINTS.paymentOnly;
    return '';
  };

  const wrapperStyles = inBuilder
    ? { '--formTextAlign': builderIntroDetails?.intro_alignment }
    : {
      '--formFont': form_font,
      '--formTextColor': toHex(form_text_color),
      '--formTextAlign': intro_alignment,
    };

  const buttonStyles = {
    '--buttonBackground': calculateButtonBackground(),
    '--buttonColor': calculateButtonColor(),
  };

  const formTitleSize = { '--titleFontSize': inBuilder ? '25px' : form_title_font_size };

  function calculateButtonBackground() {
    if (userEnableIntroPage && userIntroButtonColor) {
      return toHex(userIntroButtonColor);
    }
    return toHex(submission_button_background);
  }

  function calculateButtonColor() {
    if (!userIntroButtonColor) {
      return toHex(design?.button_text_color);
    }
    return '#fff';
  }

  const getButtonText = () => {
    if (!reshowIntroPage) {
      if (userEnableIntroPage) {
        return userIntroButtonText || user_custom_texts.start_form || defaultUserCustomTexts.start_form;
      } else {
        return defaultUserCustomTexts.start_form;
      }
    } else {
      return user_custom_texts.continue_to_form || defaultUserCustomTexts.continue_to_form;
    }
  };

  const deleteUniqueIdFromQuizList = async (id) => {
    try {
      setIsProcessingQuizId(true);
      const { status, statusText, error } = await fplsFetch({
        url: `/api/v2/public/timer/${window.FORM_ID}`,
        method: 'PUT',
        data: { unique_id: id, form_id: window.FORM_ID },
      });
      if (status === 'OK') return status;
      if (status === 404 || status === '404') {
        setQuizIdError(`${statusText}: ${error}`);
        return status;
      }
    } catch (error) {
      const newError = `There was an error updating the form owners unique ids list ${error}`;
      setQuizIdError(newError);
      console.error(newError);
    } finally {
      setIsProcessingQuizId(false);
    }
  };

  const doHideIntroPage = () => {
    if (inBuilder) {
      handleDemoStartForm();
      return;
    }

    if (isEditedByUser || (!collectRespondentEmail && !requestQuizUniqueId)) {
      dispatch(hideIntroPage());
      return;
    }

    const respondentEmail = collectRespondentEmail && emailRef?.current.value;
    const quizId = requestQuizUniqueId && quizIdRef?.current.value?.trim().toLowerCase();
    const isQuizIdAvailable = quizId && timerUniqueIds.values.map((tx) => tx.toLowerCase()).includes(quizId);

    setQuizIdError(isQuizIdAvailable ? '' : quizIdRecognitionError);

    if (
      /^\s*$/.test(respondentEmail) ||
      (enableTimer && timerPurpose === 'quiz' && (quizId === '' || (requestQuizUniqueId && !isQuizIdAvailable)))
    ) {
      return;
    }

    batch(async () => {
      if (respondentEmail) {
        dispatch(updateRespondentEmail(respondentEmail));
        if (!requestQuizUniqueId) dispatch(hideIntroPage());
      }

      if (isQuizIdAvailable) {
        const status = await deleteUniqueIdFromQuizList(quizId);
        if (status === 'OK') {
          dispatch(updateRespondentQuizTimerInfo({ quizId }));
          dispatch(hideIntroPage());
        }
      }
    });
  };

  const doSubmit = (e) => {
    e.preventDefault();
    if (!reshowIntroPage) doHideIntroPage();
    else dispatch(hideIntroPage());
  };

  useEffect(() => {
    if (!inBuilder && form_font) {
      WebFont.load({
        google: {
          families: [`${form_font}:400,500,700`],
        },
      });
    }
  }, []);

  return (
    <>
      <IntroWrapper formPageStyles={!inBuilder && formStyles(formDesign)} style={wrapperStyles} customCss={newCss}>
        <IntroBody>
          {inBuilder && (
            <FormTitle style={formTitleSize} underline={underline_title}>
              {builderForm?.name || name}
            </FormTitle>
          )}

          {(inCustomise || inForm) && <FormHeader inIntro formName={name} formDesign={formDesign} />}

          <form onSubmit={doSubmit}>
            {hasIntroMessage && (
              <MarkDownWrapper>
                <ReactMarkdown className="pre-wrap" escapeHtml={false} linkTarget="_blank" source={userIntroMessage} />
              </MarkDownWrapper>
            )}

            {showFormPreRequisites && (
              <FormPreRequisite>
                {collectRespondentEmail && (
                  <RespondentInfo>
                    <h4>{user_custom_texts.collect_email_text || defaultUserCustomTexts.collect_email_text}</h4>
                    <input required type="email" ref={emailRef} disabled={isProcessingQuizId} />
                    <span>{collectRespondentEmailHint()}</span>
                  </RespondentInfo>
                )}

                {requestQuizUniqueId && (
                  <RespondentInfo>
                    <h4>Enter your unique identifier</h4>
                    <input disabled={isProcessingQuizId} type="text" ref={quizIdRef} required />
                    {quizIdError && <span id="error">{quizIdError}</span>}
                  </RespondentInfo>
                )}
              </FormPreRequisite>
            )}

            <button type="submit" onSubmit={doSubmit} style={buttonStyles} disabled={isProcessingQuizId}>
              {getButtonText()}
            </button>
          </form>
        </IntroBody>
      </IntroWrapper>
    </>
  );
};

IntroPage.defaultProps = {
  handleDemoStartForm: () => null,
};

IntroPage.propTypes = {
  handleDemoStartForm: PropTypes.func,
};

export default IntroPage;
